/**
 * @fileoverview Milestone Calendar Utilities
 * 
 * Provides utility functions for organizing, filtering, and displaying milestones
 * in calendar and timeline views. Handles responsibility assignment, status indicators,
 * and visual styling based on user roles and milestone states.
 * 
 * @module lib/milestoneCalendarUtils
 */

import { MilestoneWithJob, MilestoneActionOwner } from '@/lib/types/milestone';
import { UserRole } from '@/lib/auth';
import { CheckCircle2, AlertCircle } from 'lucide-react';

/**
 * Groups milestones by their due date for calendar display
 * 
 * Organizes milestones into a map keyed by ISO date strings (YYYY-MM-DD).
 * Multiple milestones on the same day are grouped together for easy rendering.
 * 
 * @param milestones - Array of milestones with associated job data
 * @returns Map of date strings to milestone arrays
 * 
 * @example
 * ```typescript
 * const grouped = groupMilestonesByDate(milestones);
 * // grouped.get('2024-12-01') returns all milestones due on Dec 1
 * for (const [date, items] of grouped) {
 *   console.log(`${date}: ${items.length} milestones`);
 * }
 * ```
 */
export const groupMilestonesByDate = (milestones: MilestoneWithJob[]): Map<string, MilestoneWithJob[]> => {
  const grouped = new Map<string, MilestoneWithJob[]>();
  
  milestones.forEach((milestone) => {
    const dateKey = new Date(milestone.due_date).toISOString().split('T')[0];
    const existing = grouped.get(dateKey) || [];
    grouped.set(dateKey, [...existing, milestone]);
  });
  
  return grouped;
};

/**
 * Determines if a milestone is the responsibility of the current user role
 * 
 * Compares the milestone's action_owner field with the current user's role.
 * Admins are never considered responsible for milestones (they oversee, not execute).
 * 
 * @param milestone - The milestone with job data to check
 * @param userRole - The current user's role ('client', 'vendor', or 'admin')
 * @returns True if the milestone is assigned to the user's role
 * 
 * @example
 * ```typescript
 * const isMyResponsibility = isMilestoneUserResponsibility(milestone, 'vendor');
 * if (isMyResponsibility) {
 *   // Show as high priority action item
 * }
 * ```
 */
export const isMilestoneUserResponsibility = (
  milestone: MilestoneWithJob, 
  userRole: UserRole
): boolean => {
  if (userRole === 'admin') return false;
  return milestone.action_owner === userRole;
};

/**
 * Gets the appropriate color classes for a milestone indicator based on status and responsibility
 * 
 * Returns Tailwind CSS class strings for styling milestone indicators:
 * - Completed: Green tones (brighter if user's responsibility)
 * - Overdue: Red/destructive tones (brighter if user's responsibility)
 * - Pending: Primary or muted tones based on responsibility
 * 
 * User-responsible milestones get more prominent styling to draw attention.
 * 
 * @param milestone - The milestone with job data
 * @param userRole - The current user's role for responsibility check
 * @returns Tailwind CSS class string for background and border colors
 * 
 * @example
 * ```typescript
 * const colorClass = getMilestoneIndicatorColor(milestone, 'client');
 * // Returns: 'bg-primary/60 border-primary' (if client's responsibility)
 * ```
 */
export const getMilestoneIndicatorColor = (
  milestone: MilestoneWithJob,
  userRole: UserRole
): string => {
  const isUserResponsibility = isMilestoneUserResponsibility(milestone, userRole);
  
  if (milestone.is_completed) {
    return isUserResponsibility 
      ? 'bg-green-500/60 border-green-500' 
      : 'bg-green-500/40 border-green-300';
  }
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dueDate = new Date(milestone.due_date);
  dueDate.setHours(0, 0, 0, 0);
  const isOverdue = dueDate < today && !milestone.is_completed;
  
  if (isOverdue) {
    return isUserResponsibility 
      ? 'bg-destructive/60 border-destructive' 
      : 'bg-destructive/40 border-destructive/50';
  }
  
  return isUserResponsibility 
    ? 'bg-primary/60 border-primary' 
    : 'bg-muted border-muted-foreground/40';
};

/**
 * Gets the appropriate icon component for a milestone's current status
 * 
 * Returns a Lucide React icon component (not JSX) for status visualization:
 * - Completed: CheckCircle2
 * - Overdue: AlertCircle
 * - Pending: null (no icon)
 * 
 * @param milestone - The milestone with job data
 * @returns Lucide icon component or null
 * 
 * @example
 * ```typescript
 * const Icon = getMilestoneStatusIcon(milestone);
 * if (Icon) {
 *   return <Icon className="h-4 w-4" />;
 * }
 * ```
 */
export const getMilestoneStatusIcon = (milestone: MilestoneWithJob) => {
  if (milestone.is_completed) {
    return CheckCircle2;
  }
  
  const isOverdue = new Date(milestone.due_date) < new Date() && !milestone.is_completed;
  if (isOverdue) {
    return AlertCircle;
  }
  
  return null;
};

/**
 * Gets a human-readable status label for a milestone
 * 
 * Returns a simple string indicating the milestone's current state:
 * - 'Completed': Milestone is marked as complete
 * - 'Overdue': Past due date and not completed
 * - 'Pending': Not completed but not yet overdue
 * 
 * @param milestone - The milestone with job data
 * @returns Status label string
 * 
 * @example
 * ```typescript
 * const status = getMilestoneStatusLabel(milestone);
 * <Badge variant={status === 'Overdue' ? 'destructive' : 'default'}>{status}</Badge>
 * ```
 */
export const getMilestoneStatusLabel = (milestone: MilestoneWithJob): string => {
  if (milestone.is_completed) {
    return 'Completed';
  }
  
  const isOverdue = new Date(milestone.due_date) < new Date();
  if (isOverdue) {
    return 'Overdue';
  }
  
  return 'Pending';
};

/**
 * Groups milestones by their associated job ID
 * 
 * Organizes milestones into a map keyed by job IDs. Useful for displaying
 * all milestones related to a specific job or creating job-based timeline views.
 * 
 * @param milestones - Array of milestones with associated job data
 * @returns Map of job IDs to milestone arrays
 * 
 * @example
 * ```typescript
 * const byJob = groupMilestonesByJob(allMilestones);
 * const jobMilestones = byJob.get(jobId) || [];
 * console.log(`Job has ${jobMilestones.length} milestones`);
 * ```
 */
export const groupMilestonesByJob = (milestones: MilestoneWithJob[]): Map<string, MilestoneWithJob[]> => {
  const grouped = new Map<string, MilestoneWithJob[]>();
  
  milestones.forEach((milestone) => {
    const existing = grouped.get(milestone.job_id) || [];
    grouped.set(milestone.job_id, [...existing, milestone]);
  });
  
  return grouped;
};
